import { useFragment, graphql } from "react-relay";
import { SinglePeriodMetric } from "./__generated__/SinglePeriodTable.graphql";

import { AHText, Box } from "arkh";

import React from "react";

import { PillIndicator } from "../PillIndicator";
import { SinglePeriodTableTopAndBottom$key } from "./__generated__/SinglePeriodTableTopAndBottom.graphql";
import { View } from "react-native";

import { formatDuration } from "./formatDuration";
import { Duration } from "luxon";
import { FormattedNumber } from "react-intl";
import { SinglePeriodTableTopAndBottomTableRow_SinglePeriodAnalyticsDimension$key } from "./__generated__/SinglePeriodTableTopAndBottomTableRow_SinglePeriodAnalyticsDimension.graphql";
import { useMetricToLabel } from "./metricToLabel";

const CELL_WIDTH = 135;
const FIRST_COLUMN_WIDTH = 165;

const TableRow: React.FC<{
  dimension: SinglePeriodTableTopAndBottomTableRow_SinglePeriodAnalyticsDimension$key;
  metrics: SinglePeriodMetric[];
}> = (props) => {
  const dimension = useFragment(
    graphql`
      fragment SinglePeriodTableTopAndBottomTableRow_SinglePeriodAnalyticsDimension on SinglePeriodAnalyticsDimension {
        id
        label
        color

        metrics {
          metric
          avg
          total
        }
      }
    `,
    props.dimension
  );
  return (
    <Box style={{ flexDirection: "row" }} spaceVertical="small">
      <View style={{ width: FIRST_COLUMN_WIDTH }}>
        <AHText type="subtitle" numberOfLines={1}>
          {dimension.label}
        </AHText>
      </View>
      {props.metrics.map((m) => {
        const dimensionMetric = dimension.metrics.find(
          ({ metric }) => metric === m
        );
        if (!dimensionMetric) {
          return null;
        }
        const value = dimensionMetric.avg ?? dimensionMetric.total ?? 0;
        const metric = dimensionMetric.metric;
        if (metric === "ENGAGEMENT") {
          return (
            <View key={metric} style={{ width: CELL_WIDTH }}>
              <PillIndicator percentage={value * 100} width={CELL_WIDTH - 35} />
            </View>
          );
        }
        if (metric === "TODO_RESOLVED_AVG_TIME") {
          return (
            <View key={metric} style={{ width: CELL_WIDTH }}>
              <AHText>
                {formatDuration(Duration.fromObject({ seconds: value }))}
              </AHText>
            </View>
          );
        }
        return (
          <View key={metric} style={{ width: CELL_WIDTH }}>
            <AHText>
              <FormattedNumber value={value} />
            </AHText>
          </View>
        );
      })}
    </Box>
  );
};

const MAX_ROWS = 5;
export function SinglePeriodTableTopAndBottom(props: {
  data: SinglePeriodTableTopAndBottom$key;

  title?: React.ReactNode;
}) {
  const data = useFragment(
    graphql`
      fragment SinglePeriodTableTopAndBottom on SinglePeriodAnalytics {
        metrics {
          isAggregated
          metric
        }
        dimensions {
          id
          label

          ...SinglePeriodTableTopAndBottomTableRow_SinglePeriodAnalyticsDimension
        }
      }
    `,
    props.data
  );

  const allMetrics = data.metrics.map((m) => m.metric);
  const _METRICS: SinglePeriodMetric[] = [
    "ENGAGEMENT",
    "PRODUCT_DATE_TRACKER_REGISTERED",
    "TASK_EXECUTIONS_COMPLETED",
    "TODO_CREATED",
    "TODO_RESOLVED",
  ];
  const METRICS = _METRICS.filter((m) => allMetrics.includes(m));

  const topDimensions = data.dimensions.slice(0, MAX_ROWS);
  const bottomDimensions = data.dimensions.slice(-MAX_ROWS);

  const metricToLabel = useMetricToLabel();

  return (
    <Box
      color="surface"
      cornerRadius="large"
      spaceHorizontal="medium"
      spaceVertical="medium"
      borderColor="border-primary"
      style={{ borderWidth: 1, flex: 1 }}
    >
      <Box spaceVertical="medium" spaceHorizontal="medium">
        <Box style={{ flexDirection: "row" }}>
          <View style={{ width: FIRST_COLUMN_WIDTH }}>
            <AHText type="subtitle">{props.title}</AHText>
          </View>
          {METRICS.map((metric) => {
            const [label, subLabel] = metricToLabel(metric);
            const isAggregated = data.metrics.find(
              (m) => m.metric === metric
            )?.isAggregated;
            return (
              <View key={metric} style={{ width: CELL_WIDTH }}>
                <AHText type="info-small">
                  {label} {subLabel ?? ""}
                </AHText>
                <AHText type="detail" spaceTop="xxsmall">
                  {metric === "ENGAGEMENT" || !isAggregated ? "" : "unit avg."}
                </AHText>
              </View>
            );
          })}
        </Box>
        <Label>Top</Label>
        {topDimensions.map((dimension) => {
          return (
            <TableRow
              key={dimension.id}
              dimension={dimension}
              metrics={METRICS}
            />
          );
        })}
        {data.dimensions.length > MAX_ROWS ? (
          <>
            <Label>Bottom</Label>
            {bottomDimensions.map((dimension) => {
              return (
                <TableRow
                  key={dimension.id}
                  dimension={dimension}
                  metrics={METRICS}
                />
              );
            })}
          </>
        ) : null}
      </Box>
    </Box>
  );
}

function Label(props: { children: React.ReactNode }) {
  return (
    <AHText type="detail" spaceTop="medium" spaceBottom="small">
      {props.children}
    </AHText>
  );
}
